import React, { useState, useEffect } from 'react';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // CMS data state
    const [contactItems, setContactItems] = useState([]);
    const [company, setCompany] = useState(null);
    const [cmsLoading, setCmsLoading] = useState(true);

    // Fetch CMS data on component mount
    useEffect(() => {
        fetchContactPageData();
    }, []);

    const fetchContactPageData = async () => {
        try {
            const response = await axios.get(`${API_URL}/contact-page/page_data/`);
            if (response.data.success) {
                setContactItems(response.data.contact_items);
                setCompany(response.data.company);
            }
        } catch (error) {
            console.error('Failed to fetch contact page data:', error);
        } finally {
            setCmsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (successMessage) setSuccessMessage('');
        if (errorMessage) setErrorMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const response = await axios.post(`${API_URL}/contact/`, {
                full_name: formData.name,
                email: formData.email,
                phone_number: formData.phone,
                subject: formData.subject,
                message: formData.message
            });

            setSuccessMessage(response.data.message || 'Thank you for contacting us! We will get back to you soon.');

            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });

            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);

        } catch (error) {
            console.error('Contact form error:', error);

            if (error.response?.data) {
                const errors = error.response.data;
                let errorMsg = 'Please fix the following errors:\n';

                Object.keys(errors).forEach(key => {
                    if (Array.isArray(errors[key])) {
                        errorMsg += `- ${key}: ${errors[key].join(', ')}\n`;
                    } else {
                        errorMsg += `- ${key}: ${errors[key]}\n`;
                    }
                });

                setErrorMessage(errorMsg);
            } else {
                setErrorMessage('Failed to send message. Please check your internet connection and try again.');
            }

            setTimeout(() => {
                setErrorMessage('');
            }, 8000);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to render line breaks
    const renderWithLineBreaks = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < text.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    };

    return (
        <>
            <HeaderOne />

            <div className="contact-container">
                {/* Hero Section */}
                <section className="contact-hero-section">
                    <div className="hero-content">
                        <h1 className="hero-title">Contact Us</h1>
                        <p className="hero-subtitle">Get in touch with us today</p>
                    </div>
                </section>

                {/* Main Content */}
                <section className="contact-main-content">
                    <div className="content-wrapper">
                        <div className="contact-layout">
                            {/* Left Column - Contact Form */}
                            <div className="contact-form-section">
                                <h2>Get in Touch With Us</h2>
                                <p className="section-subtitle">
                                    Have a question or ready to get started? Fill out the form below and we'll get back to you as soon as possible.
                                </p>

                                {/* Success Message */}
                                {successMessage && (
                                    <div className="alert alert-success" style={{
                                        padding: '15px',
                                        marginBottom: '20px',
                                        backgroundColor: '#d4edda',
                                        border: '1px solid #c3e6cb',
                                        borderRadius: '8px',
                                        color: '#155724',
                                        animation: 'slideDown 0.3s ease-in-out'
                                    }}>
                                        <strong>✅ Success!</strong> {successMessage}
                                    </div>
                                )}

                                {/* Error Message */}
                                {errorMessage && (
                                    <div className="alert alert-danger" style={{
                                        padding: '15px',
                                        marginBottom: '20px',
                                        backgroundColor: '#f8d7da',
                                        border: '1px solid #f5c6cb',
                                        borderRadius: '8px',
                                        color: '#721c24',
                                        whiteSpace: 'pre-line',
                                        animation: 'slideDown 0.3s ease-in-out'
                                    }}>
                                        <strong>❌ Error!</strong> {errorMessage}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="contact-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name">Full Name *</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder="Your Full Name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="email">Email Address *</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="Your Email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="phone">Phone Number *</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                placeholder="(902) 555-1234"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="subject">Subject *</label>
                                            <input
                                                type="text"
                                                id="subject"
                                                name="subject"
                                                placeholder="What is this about?"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="message">Message *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            placeholder="Please tell us more about your request..."
                                            rows="6"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={loading}
                                        style={{
                                            opacity: loading ? 0.6 : 1,
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loading ? 'Sending...' : 'Send Message'}
                                    </button>
                                </form>
                            </div>

                            {/* Right Column - Dynamic Contact Info from CMS */}
                            <div className="contact-info-section">
                                <h2>Contact Information</h2>

                                {cmsLoading ? (
                                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                        <p>Loading contact information...</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Dynamic Contact Items */}
                                        {contactItems.map((item) => (
                                            <div key={item.id} className="info-card">
                                                <h3>
                                                    <span
                                                        style={{
                                                            display: 'inline-block',
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '50%',
                                                            backgroundColor: item.color,
                                                            lineHeight: '40px',
                                                            textAlign: 'center',
                                                            marginRight: '10px',
                                                            fontSize: '1.5rem'
                                                        }}
                                                    >
                                                        {item.icon}
                                                    </span>
                                                    {item.title}
                                                </h3>
                                                <p className="info-text">{renderWithLineBreaks(item.content)}</p>
                                                {item.subcontent && (
                                                    <p className="info-subtitle">{renderWithLineBreaks(item.subcontent)}</p>
                                                )}
                                            </div>
                                        ))}

                                        {/* Dynamic Social Media Links with Font Awesome Icons */}
                                        {company && company.social_media && company.social_media.length > 0 && (
                                            <div className="follow-section">
                                                <h3>Follow Us</h3>
                                                <div className="social-links" style={{
                                                    display: 'flex',
                                                    gap: '15px',
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap',
                                                    marginTop: '15px'
                                                }}>
                                                    {company.social_media.map((social, index) => {
                                                        // Map platform to Font Awesome icon class and color
                                                        const getSocialConfig = (platform) => {
                                                            const configs = {
                                                                'Facebook': { icon: 'fab fa-facebook-f', color: '#3b5998' },
                                                                'Instagram': { icon: 'fab fa-instagram', color: '#ac2bac' },
                                                                'Twitter': { icon: 'fab fa-twitter', color: '#55acee' },
                                                                'LinkedIn': { icon: 'fab fa-linkedin-in', color: '#0082ca' },
                                                                'YouTube': { icon: 'fab fa-youtube', color: '#ed302f' },
                                                                'Pinterest': { icon: 'fab fa-pinterest', color: '#c61118' },
                                                                'TikTok': { icon: 'fab fa-tiktok', color: '#000000' },
                                                                'WhatsApp': { icon: 'fab fa-whatsapp', color: '#25d366' },
                                                                'Telegram': { icon: 'fab fa-telegram', color: '#0088cc' },
                                                                'Reddit': { icon: 'fab fa-reddit-alien', color: '#ff4500' },
                                                                'Snapchat': { icon: 'fab fa-snapchat-ghost', color: '#fffc00' },
                                                                'GitHub': { icon: 'fab fa-github', color: '#333333' },
                                                                'Dribbble': { icon: 'fab fa-dribbble', color: '#ec4a89' },
                                                                'Slack': { icon: 'fab fa-slack-hash', color: '#481449' },
                                                                'Vimeo': { icon: 'fab fa-vimeo-v', color: '#1ab7ea' }
                                                            };
                                                            return configs[platform] || { icon: 'fas fa-link', color: '#17a2b8' };
                                                        };

                                                        const config = getSocialConfig(social.platform);

                                                        return (
                                                            <a
                                                                key={index}
                                                                href={social.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={social.platform}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#ffffff',
                                                                    border: `2px solid ${config.color}`,
                                                                    textDecoration: 'none',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.1)';
                                                                    e.currentTarget.style.backgroundColor = config.color;
                                                                    e.currentTarget.style.boxShadow = `0 6px 15px ${config.color}40`;
                                                                    const icon = e.currentTarget.querySelector('i');
                                                                    if (icon) icon.style.color = '#ffffff';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                                                    const icon = e.currentTarget.querySelector('i');
                                                                    if (icon) icon.style.color = config.color;
                                                                }}
                                                            >
                                                                <i
                                                                    className={config.icon}
                                                                    style={{
                                                                        fontSize: '1.5rem',
                                                                        color: config.color,
                                                                        transition: 'color 0.3s ease'
                                                                    }}
                                                                ></i>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                    </>
                                )}
                            </div>
                        </div>

                        {/* Dynamic Map Section */}
                        {company && company.map_link && (
                            <div className="map-section" style={{
                                marginTop: '40px',
                                width: '100%'
                            }}>
                                <h3 style={{
                                    textAlign: 'center',
                                    marginBottom: '20px',
                                    color: '#17a2b8',
                                    fontSize: '1.5rem',
                                    fontWeight: '600'
                                }}>
                                 Our Location
                                </h3>
                                <div
                                    dangerouslySetInnerHTML={{ __html: company.map_link }}
                                    style={{
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        height: '280px'
                                    }}
                                />
                            </div>
                        )}

                        {/* Dynamic Footer Section */}
                        {company && company.contact_footer_text && (
                            <div className="contact-footer" style={{
                                marginTop: '40px',
                                textAlign: 'center',
                                padding: '20px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '12px'
                            }}>
                                <p style={{ margin: 0, color: '#666', fontSize: '1rem' }}>
                                    {company.contact_footer_text}
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <BottomFooter />

            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                /* Map iframe styling */
                .map-section iframe {
                    width: 100% !important;
                    height: 280px !important;
                    border: 0 !important;
                    border-radius: 12px !important;
                }
                
                /* Ensure proper map container */
                .map-section > div {
                    width: 100%;
                }
            `}</style>
        </>
    );
};

export default Contact;
